import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Loader2 } from 'lucide-react';
import { ticketsApi, type TicketBrief, type TicketStatus } from '../api/tickets';
import { fmtTime, ticketNumber, STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, SLA_COLOR, ticketStatusColor, ticketStatusLabel } from '../lib/format';

const STATUS_FILTERS: Array<{ value: '' | TicketStatus; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'open', label: 'В работе' },
  { value: 'pending_customer', label: 'Ждут клиента' },
  { value: 'resolved', label: 'Решённые' },
  { value: 'closed', label: 'Закрытые' },
];

export default function TicketList() {
  const [items, setItems] = useState<TicketBrief[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<'' | TicketStatus>('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ticketsApi.list({ status: status || undefined, q: q || undefined });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(typeof res?.total === 'number' ? res.total : 0);
    } catch (e: any) {
      setError(e?.message ?? 'Ошибка загрузки');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="flex gap-1">
          {STATUS_FILTERS.map(f => (
            <button key={f.value || 'all'}
              onClick={() => setStatus(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase transition ${
                status === f.value ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Поиск по теме…"
            className="bg-slate-900 border border-slate-700 rounded-full py-1.5 pl-10 pr-4 text-sm focus:border-blue-500 outline-none w-64 text-white" />
        </div>
        <Link to="/new"
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2 rounded-lg transition">
          <Plus size={16} /> Создать
        </Link>
      </div>

      {error && <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-2 rounded mb-3 text-sm">{error}</div>}

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 text-[10px] text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-4">№</th>
              <th className="text-left py-3 px-4">Тема</th>
              <th className="text-left py-3 px-4">Статус</th>
              <th className="text-left py-3 px-4">Приоритет</th>
              <th className="text-left py-3 px-4">Заявитель</th>
              <th className="text-center py-3 px-4">SLA</th>
              <th className="text-right py-3 px-4">Создан</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500">
                <Loader2 className="animate-spin inline" /> Загружаю…
              </td></tr>
            ) : (items || []).length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500">
                Нет тикетов. <Link to="/new" className="text-blue-400 underline">Создать первый</Link>
              </td></tr>
            ) : (items || []).map(t => (
              <tr key={t.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                <td className="py-3 px-4 font-mono text-blue-300">
                  <Link to={`/tickets/${t.id}`}>{ticketNumber(t.number)}</Link>
                </td>
                <td className="py-3 px-4">
                  <Link to={`/tickets/${t.id}`} className="text-white hover:text-blue-400">{t.subject}</Link>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${ticketStatusColor(t)}`}>
                    {ticketStatusLabel(t)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${PRIORITY_COLOR[t.priority]}`}>
                    {t.priority}
                  </span>
                </td>
                <td className="py-3 px-4 text-slate-400">
                  {t.requester_name || t.requester_email || <span className="text-slate-600">—</span>}
                </td>
                <td className="py-3 px-2 text-center">
                  {t.sla_state && t.sla_label ? (
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono border ${SLA_COLOR[t.sla_state]}`}
                          title={t.sla_state === 'ok' ? 'В рамках SLA' : `SLA: ${t.sla_state}`}>
                      {t.sla_label}
                    </span>
                  ) : <span className="text-slate-700 text-[10px]">—</span>}
                </td>
                <td className="py-3 px-4 text-right text-slate-500 text-xs">{fmtTime(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-600 mt-2 text-right">Всего: {total}</div>
    </div>
  );
}
